import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Camera, Video, Users, PartyPopper, Link2 } from "lucide-react";
import { getMyChatRooms } from "../../services/chatService";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import Badge from "../../components/common/Badge";
import { toast } from "react-toastify";
import { color, radius, shadow } from "../../styles/theme";

const RoomList = () => {
  const { t } = useTranslation("rooms");
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getMyChatRooms("Personal")
      .then((res) => {
        const list = res.data?.Result || [];
        // A promoter only ever has a single @Room — skip the picker and go
        // straight in, same as mobile does. Falls back to showing the list
        // itself if that assumption is ever wrong (0 or 2+ rooms).
        if (list.length === 1) {
          navigate(`/rooms/${list[0].Id}/galleries`, { state: { room: list[0] }, replace: true });
          return;
        }
        setRooms(list);
      })
      .catch(() => toast.error(t("roomList.toasts.loadFailed")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRoomClick = (room) => {
    navigate(`/rooms/${room.Id}/galleries`, { state: { room } });
  };

  if (loading) return <Loader />;

  return (
    <div>
      <h2 style={s.heading}>
        {t("roomList.heading")}
        <span style={s.count}>{t("roomList.roomCount", { count: rooms.length })}</span>
      </h2>

      {rooms.length === 0 ? (
        <EmptyState icon={Users} text={t("roomList.emptyText")} />
      ) : (
        <div style={s.list}>
          {rooms.map((room) => (
            <div key={room.Id} className="icon-tile" style={s.card} onClick={() => handleRoomClick(room)}>
              <div style={s.avatarWrap}>
                {room.LogoUrl ? (
                  <img src={room.LogoUrl.replace("\\", "/")} alt={room.Name} style={s.avatar} onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <div style={s.avatarFallback}>{room.Name?.charAt(0).toUpperCase() || "R"}</div>
                )}
              </div>

              <div style={s.info}>
                <div style={s.roomName}>{room.Name}</div>
                {room.LastMessage && room.LastMessage.length > 0 && (
                  <div style={s.lastMsg}>
                    {room.LastMessage[0].MessageType === "image" ? (
                      <><Camera size={12} strokeWidth={2} /> {t("roomList.photoLabel")}</>
                    ) : room.LastMessage[0].MessageType === "video" ? (
                      <><Video size={12} strokeWidth={2} /> {t("roomList.videoLabel")}</>
                    ) : room.LastMessage[0].Message}
                  </div>
                )}
                <div style={s.meta}>
                  <span style={s.metaItem}><Users size={12} strokeWidth={2} /> {t("roomList.usersLabel", { count: room.TotalUsers })}</span>
                  <span style={s.metaItem}><PartyPopper size={12} strokeWidth={2} /> {t("roomList.eventsLabel", { count: room.TotalEvents })}</span>
                  <Badge variant="primary">{room.RoomType}</Badge>
                </div>
              </div>

              <div style={s.right}>
                {room.LastMessage?.[0]?.Timestamp && (
                  <div style={s.time}>
                    {new Date(room.LastMessage[0].Timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </div>
                )}
                {room.UnreadMessageCount > 0 && <div style={s.unreadBadge}>{room.UnreadMessageCount}</div>}
                {room.ChatRoomLink && (
                  <a href={room.ChatRoomLink} target="_blank" rel="noopener noreferrer" style={s.linkBtn} onClick={(e) => e.stopPropagation()}>
                    <Link2 size={15} strokeWidth={2} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const s = {
  heading: { margin: "0 0 24px", fontSize: 21, fontWeight: 800, color: color.textPrimary, display: "flex", alignItems: "center", gap: 12, letterSpacing: -0.4 },
  count: { fontSize: 12.5, fontWeight: 600, color: color.textMuted, background: color.gray100, padding: "3px 12px", borderRadius: radius.pill },
  list: { display: "flex", flexDirection: "column", gap: 12, maxWidth: 680 },
  card: {
    background: color.surface, borderRadius: radius.lg, padding: "16px 20px", boxShadow: shadow.sm,
    border: `1px solid ${color.border}`, display: "flex", alignItems: "center", gap: 16, cursor: "pointer",
  },
  avatarWrap: { flexShrink: 0 },
  avatar: { width: 52, height: 52, borderRadius: "50%", objectFit: "cover", display: "block", border: `2px solid ${color.primary100}` },
  avatarFallback: {
    width: 52, height: 52, borderRadius: "50%", background: color.primary500, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 20,
  },
  info: { flex: 1, minWidth: 0 },
  roomName: { fontWeight: 700, fontSize: 15, color: color.textPrimary, marginBottom: 5 },
  lastMsg: {
    display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: color.textMuted,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 7, maxWidth: 380,
  },
  meta: { display: "flex", alignItems: "center", gap: 14, fontSize: 12, color: color.textSecondary },
  metaItem: { display: "inline-flex", alignItems: "center", gap: 4 },
  right: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 },
  time: { fontSize: 11, color: color.textMuted },
  unreadBadge: {
    background: color.primary500, color: "#fff", borderRadius: "50%", width: 20, height: 20,
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700,
  },
  linkBtn: { color: color.primary500, display: "flex" },
};

export default RoomList;
